// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * LE DÉLAI DE RENDU, dit d'une SEULE façon (RQ, 20/09/2026).
 *
 * ── Ce que ça répare ────────────────────────────────────────────────────────
 *
 * Le délai de rendu se présentait de CINQ façons, portées par quatre champs et
 * deux unités, et aucune ne se déduisait des autres :
 *
 *   · la pastille du catalogue patient          `delaiRendu`  texte libre
 *   · la ligne en tête de fiche                 `delaiRendu`  texte libre
 *   · « · rendu 3 j » sous la matière           `delaiRenduHeures`  heures→jours
 *   · quatre colonnes du catalogue plat         les quatre champs
 *   · le registre de sous-traitance             `delaiRendu`  texte libre
 *
 * Le texte libre portait onze graphies pour ce qui ne fait que cinq notions —
 * dont « 5 jours d'incubation » et « 5 jours d’incubation », deux graphies pour
 * une valeur, l'apostrophe droite contre la courbe. Il mêlait trois registres
 * incompatibles : le jour relatif (`J2–J3`), la durée (`21 à 28 jours`) et le
 * composite (`Direct J0 ; culture 1 à 5 j`, qui porte DEUX délais).
 *
 * C'est exactement le défaut que le catalogue des champs a déjà payé sur les
 * prix, et dont il porte la leçon écrite (`FIELD_CATALOG`, attribut `unite`) :
 * « Unité affichée à côté de la valeur, jamais stockée avec elle — la mêler au
 * nombre est ce qui avait rendu les prix incomparables entre eux. »
 *
 * ── La règle, une seule ─────────────────────────────────────────────────────
 *
 * La DONNÉE est un nombre d'HEURES (`delaiRenduHeures`), décidé le 14/08/2026
 * pour que le délai soit suivable — globalement et par examen. Le RENDU est
 * calculé ici, et nulle part ailleurs : en HEURES sous deux jours, en JOURS
 * au-delà. Une unité, deux formes, zéro graphie libre.
 *
 * ⚠️ ARRONDI AU SUPÉRIEUR, et c'est un correctif. Les deux copies de cette
 * règle (`FicheRow`, `FicheDetail`) arrondissaient au plus proche : un délai de
 * 49 h s'annonçait « 2 j », soit 48 h — une promesse tenue une heure trop tard,
 * à chaque fois, sans que rien ne le signale. Un délai est un ENGAGEMENT : on
 * n'annonce jamais moins que ce qu'on met.
 *
 * ⚠️ Ne JAMAIS éditer les copies déposées dans `src/shared/` des trois
 * applications : la source est ICI, et la copie se fait par
 * `bash shared/sync.sh`.
 */

/**
 * Le seuil de bascule heures → jours, en heures.
 *
 * Deux jours pleins : en deçà, « 36 h » dit quelque chose que « 2 j » ne dit
 * pas (le résultat tombe le lendemain matin, pas le lendemain soir) ; au-delà,
 * personne ne compte en heures et « 168 h » se relit deux fois.
 *
 * Il est ICI, nommé, et non recopié dans les écrans — c'est la valeur qu'on
 * changera d'un seul geste si le laboratoire en décide une autre.
 */
export const SEUIL_JOURS_H = 48;

/** La tranche, en heures, bornes comprises, où le délai s'annonce « < N h »
 *  (Ines, 19/10/2026). Nommée ici pour se changer d'un seul geste. */
export const PLAGE_INFERIEUR_A: [number, number] = [24, 48];

/** Ce qu'on affiche quand aucun délai n'est connu. Un tiret cadratin, jamais
 *  une chaîne vide : une case vide se lit « immédiat » ou « on a oublié la
 *  colonne », le tiret se lit « non renseigné ». */
export const DELAI_INCONNU = '—';

/**
 * Le registre d'affichage — le même nombre, trois publics.
 *
 * `court`   : la pastille, le tableau, la colonne. La place manque.  « 72 h » → `3 j`
 * `phrase`  : le patient et le livret imprimé. Un délai est un plafond,
 *             et le dire (« sous ») évite de promettre une heure exacte.      → `sous 3 j`
 * `long`    : la fiche détaillée, où l'unité s'écrit en toutes lettres.       → `3 jours au plus`
 *
 * De 24 à 48 h, les trois registres disent « inférieur à » : `< 24 h`,
 * `< 48 h`, et en `long` `moins de 48 heures`.
 */
export type RegistreDelai = 'court' | 'phrase' | 'long';

/** Un délai exploitable : un entier d'heures strictement positif. `0` est
 *  refusé au même titre que `null` — un examen rendu en zéro heure n'existe
 *  pas, et laisser passer le zéro afficherait « 0 h » là où la donnée manque. */
function heuresValides(heures: unknown): number | null {
  const n = typeof heures === 'number' ? heures : Number(heures);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * Le délai de rendu, rendu lisible — LA fonction, la seule que les écrans
 * appellent.
 *
 * @param heures la donnée (`delaiRenduHeures`), en heures
 * @param registre la forme voulue ; `court` par défaut, la plus contrainte
 */
export function formaterDelai(heures: unknown, registre: RegistreDelai = 'court'): string {
  const n = heuresValides(heures);
  if (n === null) return DELAI_INCONNU;

  // Sous le seuil on garde les heures TELLES QUELLES, sans arrondi : la donnée
  // est déjà en heures, l'arrondir ici ne ferait que perdre ce qui a été saisi.
  /* DE 24 À 48 H, « INFÉRIEUR À » (Ines, 19/10/2026) : « < 24 h », « < 48 h ».
     48 h reste en heures dans cette tranche (« < 48 h », pas « 2 j »). Le
     registre `long` porte lui-même la mention d'engagement (« moins de… »,
     « … au plus ») : les écrans ne la rajoutent pas. */
  if (n >= PLAGE_INFERIEUR_A[0] && n <= PLAGE_INFERIEUR_A[1]) {
    const h = Math.ceil(n);
    return registre === 'long' ? `moins de ${h} heures` : `< ${h} h`;
  }

  const enJours = n >= SEUIL_JOURS_H;
  const valeur = enJours ? Math.ceil(n / 24) : Math.ceil(n);

  switch (registre) {
    case 'long':
      return `${enJours ? `${valeur} jour${valeur > 1 ? 's' : ''}` : `${valeur} heure${valeur > 1 ? 's' : ''}`} au plus`;
    case 'phrase':
      return `sous ${valeur} ${enJours ? 'j' : 'h'}`;
    default:
      return `${valeur} ${enJours ? 'j' : 'h'}`;
  }
}

/**
 * PLUS DE REPLI SUR LE TEXTE (Ines, 19/10/2026). « Un délai est un maximum
 * auquel on s'engage » : la reprise du 19/10
 * (`backend/scripts/reprises/delais-de-rendu.sql`) a converti chaque phrase en
 * sa borne haute, en heures. `delaiAffichable`, qui rendait la phrase telle
 * quelle faute d'heures, est retirée : elle laissait coexister deux manières de
 * dire un délai. Le texte `delaiRendu` reste en base comme trace, il ne
 * s'affiche plus nulle part. Une fiche sans heures affiche « — ».
 */

/** Les jours de la semaine, dans l'ordre et sous leur nom affiché. */
const JOURS_SEMAINE: [string, string][] = [
  ['lun', 'lundi'], ['mar', 'mardi'], ['mer', 'mercredi'], ['jeu', 'jeudi'],
  ['ven', 'vendredi'], ['sam', 'samedi'], ['dim', 'dimanche'],
];

/**
 * Les JOURS DE RÉALISATION, rendus lisibles — ils remplacent le « délai de
 * réalisation » en texte libre (Ines, 19/10/2026).
 *
 * Vide = tous les jours d'ouverture (cf. schéma `fiche.joursRealisation`) :
 * c'est le cas général, et le dire évite qu'une case vide se lise « jamais ».
 * Les jours se rendent dans l'ordre de la semaine, quel que soit l'ordre saisi.
 */
export function formaterJours(jours: unknown): string {
  const liste = Array.isArray(jours) ? jours.map((j) => String(j)) : [];
  const retenus = JOURS_SEMAINE.filter(([c]) => liste.includes(c));
  if (!retenus.length || retenus.length === JOURS_SEMAINE.length) return 'tous les jours d’ouverture';
  return retenus.map(([, nom]) => nom).join(', ');
}

/**
 * Le délai d'une MATIÈRE, qui l'emporte sur celui de l'examen quand il existe.
 *
 * La règle est déjà écrite côté serveur (`backend/src/matrices.ts`, `delaiPour`)
 * et son motif y est donné : « 72 h sur sang et trois semaines sur liquide
 * amniotique ne se résument pas à un délai unique — et c'est ce délai que le
 * manuel promet au prescripteur. » Elle est reprise ici parce que les écrans
 * reçoivent les deux valeurs et doivent trancher sans rappeler le serveur.
 *
 * ⚠️ `0` et `null` se valent ici : l'un comme l'autre veulent dire « cette
 * matière n'a pas son propre délai », et retomber sur l'examen est la seule
 * réponse juste. Traiter `0` comme une valeur afficherait « 0 h » pour une
 * matière dont personne n'a rien dit.
 */
export function delaiDeLaMatiere(heuresMatiere: unknown, heuresExamen: unknown): number | null {
  return heuresValides(heuresMatiere) ?? heuresValides(heuresExamen);
}
